import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  api,
  errorMessage,
  formatDate,
  formatMoney,
  DetailRow,
} from "./assetsShared";
import "./Assets.css";

/* ================= CONSTANTS ================= */
const HARDWARE_TYPES = [
  "Keyboard",
  "Mouse",
  "Graphic Card",
  "CPU",
  "Screen",
  "Earphones",
  "RAM",
  "Processor",
  "Laptop",
];

const BRANDS = ["Dell", "Zebronics", "Logitech"];
const CITIES = ["Surat", "Ahmedabad", "Gandhinagar"];

const CARD_COLOR_CLASS = {
  Keyboard: "as-card-purple",
  Mouse: "as-card-blue",
  "Graphic Card": "as-card-orange",
  CPU: "as-card-red",
  Screen: "as-card-green",
  Earphones: "as-card-pink",
  RAM: "as-card-teal",
  Processor: "as-card-indigo",
  Laptop: "as-card-brown",
};

/* ================= EMPTY FORM ================= */
const emptyForm = {
  type: "",
  productBrand: "",
  connectionType: "",
  pcName: "",
  location: "",
  serialNo: "",
  invoiceNo: "",
  vendorName: "",
  vendorContactNo: "",
  vendorEmail: "",
  ipAddress: "",
  purchaseDate: "",
  warrantyEndDate: "",
  purchaseValue: "",
  assignedUser: "",
};

export default function HardwareMaster() {
  const navigate = useNavigate();

  const [hardware, setHardware] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [editId, setEditId] = useState(null);
  const [activeType, setActiveType] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const detailRef = useRef(null);

  /* ================= LOAD DATA ================= */
  const loadData = async () => {
    try {
      setError("");

      const [hardwareResponse, usersResponse] = await Promise.all([
        api.get("/hardware"),
        api.get("/users"),
      ]);

      setHardware(hardwareResponse.data || []);
      setUsers(usersResponse.data || []);
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to load hardware."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  /* ================= ONLY ACTIVE HARDWARE ================= */
  const activeHardware = useMemo(
    () => hardware.filter((h) => h.status === "ACTIVE"),
    [hardware]
  );

  /* ================= CARDS (ONLY TYPES THAT HAVE ITEMS) ================= */
  const hardwareCards = useMemo(() => {
    // known types first, then any other type that exists in data
    const typesInData = [...new Set(activeHardware.map((h) => h.type))];
    const allTypes = [
      ...HARDWARE_TYPES,
      ...typesInData.filter((type) => !HARDWARE_TYPES.includes(type)),
    ];

    return allTypes
      .map((type) => {
        const items = activeHardware.filter((h) => h.type === type);

        return {
          type,
          total: items.length,
          used: items.filter((h) => h.assignedUser).length,
          available: items.filter((h) => !h.assignedUser).length,
          cities: CITIES.map((city) => ({
            city,
            count: items.filter((h) => h.location === city).length,
          })).filter((entry) => entry.count > 0),
        };
      })
      .filter((card) => card.total > 0);
  }, [activeHardware]);

  /* ================= DETAIL ITEMS (ONLY SELECTED CARD) ================= */
  const detailItems = useMemo(
    () =>
      activeType ? activeHardware.filter((h) => h.type === activeType) : [],
    [activeHardware, activeType]
  );

  /* ================= CARD TOGGLE ================= */
  const handleCardClick = (type) => {
    // same card again = close details
    if (activeType === type) {
      setActiveType(null);
      return;
    }

    setActiveType(type);

    // auto scroll to detail section
    setTimeout(() => {
      detailRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 100);
  };

  /* ================= SAVE ================= */
  const handleSave = async (event) => {
    event.preventDefault();

    const payload = {
      ...form,
      purchaseDate: form.purchaseDate || null,
      warrantyEndDate: form.warrantyEndDate || null,
      purchaseValue: Number(form.purchaseValue) || 0,
      assignedUser: form.assignedUser || null,
    };

    try {
      setSaving(true);
      setError("");

      if (isEdit) {
        await api.put(`/hardware/${editId}`, payload);
      } else {
        await api.post("/hardware", payload);
      }

      setShowAdd(false);
      setIsEdit(false);
      setEditId(null);
      setForm(emptyForm);
      await loadData();
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to save hardware."));
    } finally {
      setSaving(false);
    }
  };

  /* ================= ADD / EDIT ================= */
  const openAdd = () => {
    setForm(emptyForm);
    setIsEdit(false);
    setEditId(null);
    setError("");
    setShowAdd(true);
  };

  const openEdit = (h) => {
    setForm({
      type: h.type || "",
      productBrand: h.productBrand || "",
      connectionType: h.connectionType || "",
      pcName: h.pcName || "",
      location: h.location || "",
      serialNo: h.serialNo || "",
      invoiceNo: h.invoiceNo || "",
      vendorName: h.vendorName || "",
      vendorContactNo: h.vendorContactNo || "",
      vendorEmail: h.vendorEmail || "",
      ipAddress: h.ipAddress || "",
      purchaseDate: h.purchaseDate?.slice(0, 10) || "",
      warrantyEndDate: h.warrantyEndDate?.slice(0, 10) || "",
      purchaseValue: h.purchaseValue || "",
      assignedUser: h.assignedUser?._id || "",
    });

    setEditId(h._id);
    setIsEdit(true);
    setError("");
    setShowAdd(true);
  };

  /* ================= DESTROY ================= */
  const handleDestroy = async (h) => {
    const note = window.prompt("Reason for destroy?");
    if (!note || !note.trim()) return;

    try {
      setError("");
      await api.post(`/hardware/${h._id}/destroy`, { note: note.trim() });
      await loadData();
    } catch (requestError) {
      setError(errorMessage(requestError, "Unable to destroy hardware."));
    }
  };

  const setField = (field, value) =>
    setForm((current) => ({ ...current, [field]: value }));

  return (
    <div className="as-page">
      <div className="as-header">
        <h2>Hardware Master</h2>

        <div className="as-header-actions">
          <button type="button" className="as-btn as-btn-primary" onClick={openAdd}>
            + Add Hardware
          </button>

          <button
            type="button"
            className="as-btn as-btn-secondary"
            onClick={() => navigate("/hardware-destroyed")}
          >
            🗑 Destroyed Hardware
          </button>
        </div>
      </div>

      {error && <div className="as-alert">{error}</div>}

      {loading && <div className="as-empty">Loading...</div>}

      {!loading && !activeHardware.length && !error && (
        <div className="as-empty">
          No hardware added yet. "+ Add Hardware" par click karke pehla item add karo.
        </div>
      )}

      {/* ================= CARDS ================= */}
      {hardwareCards.length > 0 && (
        <div className="as-card-grid">
          {hardwareCards.map((c) => (
            <div
              key={c.type}
              className={`as-card ${CARD_COLOR_CLASS[c.type] || ""} ${
                activeType === c.type ? "active" : ""
              }`}
              onClick={() => handleCardClick(c.type)}
            >
              <div className="as-card-title">
                <h3>{c.type}</h3>
                <p>Hardware</p>
              </div>

              <div className="as-stats">
                <div className="as-stat used">
                  <span>Used</span>
                  <strong>{c.used}</strong>
                </div>

                <div className="as-stat available">
                  <span>Available</span>
                  <strong>{c.available}</strong>
                </div>

                {c.cities.map((entry) => (
                  <div key={entry.city} className="as-stat city">
                    <span>{entry.city}</span>
                    <strong>{entry.count}</strong>
                  </div>
                ))}
              </div>

              <div className="as-total">
                <span>Total {c.type}</span>
                <strong>{c.total}</strong>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ================= DETAILS (ONLY WHEN A CARD IS OPEN) ================= */}
      {activeType && detailItems.length > 0 && (
        <div ref={detailRef} className="as-detail-wrapper">
          <h3 className="as-section-title">
            {activeType} <small>({detailItems.length})</small>
          </h3>

          <div className="as-detail-grid">
            {detailItems.map((h) => (
              <div key={h._id} className="as-detail-card">
                <h4 className="as-detail-title">{h.type}</h4>

                <p className="as-detail-subtitle">
                  {[h.productBrand, h.pcName].filter(Boolean).join(" – ")}
                </p>

                <DetailRow label="Type" value={h.type} />
                <DetailRow label="Brand" value={h.productBrand} />
                <DetailRow label="Connection Type" value={h.connectionType} />
                <DetailRow label="Serial No" value={h.serialNo} />
                <DetailRow label="Location" value={h.location} />
                <DetailRow label="Invoice No" value={h.invoiceNo} />
                <DetailRow label="IP Address" value={h.ipAddress} />
                <DetailRow label="Vendor Name" value={h.vendorName} />
                <DetailRow label="Vendor Contact" value={h.vendorContactNo} />
                <DetailRow label="Vendor Email" value={h.vendorEmail} />
                <DetailRow
                  label="Purchase Value"
                  value={formatMoney(h.purchaseValue)}
                />
                <DetailRow
                  label="Purchase Date"
                  value={formatDate(h.purchaseDate)}
                />
                <DetailRow
                  label="Warranty Till"
                  value={formatDate(h.warrantyEndDate)}
                />
                <DetailRow
                  label="Status"
                  value={
                    h.assignedUser
                      ? `Assigned to ${h.assignedUser.name}`
                      : "IN STOCK"
                  }
                />

                <div className="as-detail-actions">
                  <button type="button" onClick={() => openEdit(h)}>
                    ✏️ Edit
                  </button>

                  <button
                    type="button"
                    className="danger"
                    onClick={() => handleDestroy(h)}
                  >
                    🗑 Destroy
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ================= ADD / EDIT DRAWER ================= */}
      {showAdd && (
        <div className="as-drawer-overlay">
          <div className="as-drawer">
            <div className="as-drawer-header">
              <h3>{isEdit ? "Edit Hardware" : "Add Hardware"}</h3>

              <button
                type="button"
                className="as-drawer-close"
                onClick={() => setShowAdd(false)}
              >
                ✖
              </button>
            </div>

            <form className="as-form-grid" onSubmit={handleSave}>
              <label className="as-field">
                <span>Hardware Type *</span>
                <select
                  required
                  value={form.type}
                  onChange={(e) => setField("type", e.target.value)}
                >
                  <option value="">Select Hardware Type</option>
                  {HARDWARE_TYPES.map((t) => (
                    <option key={t}>{t}</option>
                  ))}
                </select>
              </label>

              <label className="as-field">
                <span>Brand *</span>
                <select
                  required
                  value={form.productBrand}
                  onChange={(e) => setField("productBrand", e.target.value)}
                >
                  <option value="">Select Brand</option>
                  {BRANDS.map((b) => (
                    <option key={b}>{b}</option>
                  ))}
                </select>
              </label>

              <label className="as-field">
                <span>Connection Type</span>
                <select
                  value={form.connectionType}
                  onChange={(e) => setField("connectionType", e.target.value)}
                >
                  <option value="">Connection Type</option>
                  <option value="Wired">Wired</option>
                  <option value="Wireless">Wireless</option>
                  <option value="Both">Both</option>
                </select>
              </label>

              <label className="as-field">
                <span>PC / System Name *</span>
                <input
                  required
                  value={form.pcName}
                  onChange={(e) => setField("pcName", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>City *</span>
                <select
                  required
                  value={form.location}
                  onChange={(e) => setField("location", e.target.value)}
                >
                  <option value="">Select City</option>
                  {CITIES.map((c) => (
                    <option key={c}>{c}</option>
                  ))}
                </select>
              </label>

              <label className="as-field">
                <span>Serial Number *</span>
                <input
                  required
                  value={form.serialNo}
                  onChange={(e) => setField("serialNo", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Invoice Number</span>
                <input
                  value={form.invoiceNo}
                  onChange={(e) => setField("invoiceNo", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Vendor Name *</span>
                <input
                  required
                  value={form.vendorName}
                  onChange={(e) => setField("vendorName", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Vendor Contact</span>
                <input
                  value={form.vendorContactNo}
                  onChange={(e) => setField("vendorContactNo", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Vendor Email</span>
                <input
                  type="email"
                  value={form.vendorEmail}
                  onChange={(e) => setField("vendorEmail", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>IP Address</span>
                <input
                  value={form.ipAddress}
                  onChange={(e) => setField("ipAddress", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Assign To</span>
                <select
                  value={form.assignedUser}
                  onChange={(e) => setField("assignedUser", e.target.value)}
                >
                  <option value="">Keep in Stock</option>
                  {users.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="as-field">
                <span>Purchase Date</span>
                <input
                  type="date"
                  value={form.purchaseDate}
                  onChange={(e) => setField("purchaseDate", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Warranty End Date</span>
                <input
                  type="date"
                  value={form.warrantyEndDate}
                  onChange={(e) => setField("warrantyEndDate", e.target.value)}
                />
              </label>

              <label className="as-field">
                <span>Purchase Value</span>
                <input
                  type="number"
                  min="0"
                  value={form.purchaseValue}
                  onChange={(e) => setField("purchaseValue", e.target.value)}
                />
              </label>

              <div className="as-drawer-footer">
                <button
                  type="submit"
                  className="as-btn as-btn-primary"
                  disabled={saving}
                >
                  {saving ? "Saving..." : isEdit ? "Update" : "Save"}
                </button>

                <button
                  type="button"
                  className="as-btn as-btn-secondary"
                  onClick={() => setShowAdd(false)}
                  disabled={saving}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}